/**
 * One Orgus Watch incident as a Needs you row (ADR 0058 decision 3, as amended): the same anatomy
 * as the rows around it (icon tile, title, one caption line), so the list reads as one. The tile
 * carries the severity (or who has it), the caption why it matters, or who took it. The incident's
 * own action and the menu (Acknowledge, Dismiss) appear on hover and focus, and stay shown under a
 * finger. The row opens to its evidence, with the action in words.
 */
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  CircleX,
  Copy,
  Ellipsis,
  FilePen,
  FlaskConical,
  Flame,
  FolderTree,
  GitBranch,
  GitCompareArrows,
  GitMerge,
  HardDrive,
  HeartPulse,
  Hourglass,
  Layers,
  Radar,
  Repeat,
  Scale,
  Server,
  ShieldX,
  Snowflake,
  Trash2,
  TriangleAlert,
  Undo2,
} from '@orgus/ui/icons';
import { useEffect, useId, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { Incident, IncidentDetector } from '@orgus/protocol';
import type { IconTileTone } from '@orgus/ui';
import { INCIDENT_STATE_LABEL } from '@orgus/protocol';
import {
  Button,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ErrorState,
  IconButton,
  IconTile,
  Input,
  Pressable,
  REVEAL_GROUP,
  RelativeTime,
  RevealRest,
  RevealSlot,
  Tooltip,
  copyText,
  cn,
  useNow,
} from '@orgus/ui';
import { useCan, useMe } from '../auth/useMe.ts';
import { useOrgus } from '../../lib/store.ts';
import { ackIncident, actOnIncident, dismissIncident } from './api.ts';
import { actionOf, detailFacts, machineCommandOf, handlingPath, fixSessionPath, revertPath, incidentMeta, incidentRowStatus, lastCheckedText, primaryActionOf, raisedText, staleCaption, staleText, whoLabel } from './model.ts';
import type { PrimaryAction } from './model.ts';
import type { WatchView } from './useWatch.ts';

type Icon = ComponentType<{ className?: string }>;

/** Each detector's mark; the tile's tone carries the severity. */
const DETECTOR_ICON: Record<IncidentDetector, Icon> = {
  'refusal-storm': ShieldX,
  'count-mismatch': Scale,
  'hub-drift': GitCompareArrows,
  'landing-failed': GitMerge,
  'main-red': GitBranch,
  'flaky-test': FlaskConical,
  'orphaned-work': FilePen,
  'forgotten-process': Server,
  'dev-server': Server,
  cleanup: Trash2,
  'disk-low': HardDrive,
  'mission-environment': ShieldX,
  'loop-stale': HeartPulse,
  'start-storm': CircleX,
  'worktrees-heavy': FolderTree,
  'backup-stale': HardDrive,
  'stuck-session': Hourglass,
  'stale-failure': CircleX,
  'cold-resumes': Snowflake,
  'context-heavy': Layers,
  'burn-without-progress': Flame,
  'failure-loop': Repeat,
  watch: Radar,
};

/** The icon for an action in a compact button: what pressing it does. */
const ACTION_ICON: Record<PrimaryAction['kind'], Icon> = { act: Undo2, open: ArrowUpRight, copy: Copy, ack: Check };

/** The tile's tone from the row's one status. */
function tileTone(tone: string | undefined): IconTileTone {
  return tone === 'working' || tone === 'danger' || tone === 'attention' ? tone : 'neutral';
}

/** Who is signed in, and whether they may act on incidents (anyone in demo mode). */
export function useIncidentActor(): { me: string | null; can: boolean } {
  const me = useMe((s) => s.me?.actor.id ?? null);
  const can = useCan('operator');
  const mock = useOrgus((s) => s.mode === 'mock');
  return { me, can: can || mock };
}

/** Runs one of the row's actions, keeping its busy state and its error (shown inline, never a toast). */
function useRun() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const run = async (key: string, work: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };
  return { busy, error, run, clear: () => setError(null) };
}

/**
 * The incident's own action, verb plus object. Compact, it is an icon button with the words in
 * its name and tooltip (the row's hover slot); otherwise a labelled button (the opened row).
 */
export function IncidentActionButton({ action, busy, onRun, compact, className }: { action: PrimaryAction; busy: boolean; onRun: () => void; compact?: boolean; className?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  const press = async () => {
    if (action.kind !== 'copy') return onRun();
    if (!(await copyText(action.text))) return onRun();
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1_600);
  };
  const Mark = copied ? Check : ACTION_ICON[action.kind];
  const label = copied ? 'Copied' : action.label;
  if (compact) return <IconButton size="xs" label={label} icon={<Mark />} loading={busy} onClick={() => void press()} className={className} aria-live={action.kind === 'copy' ? 'polite' : undefined} />;
  return (
    <Button size="sm" variant="secondary" className={className} icon={<Mark />} loading={busy} title={action.kind === 'copy' ? action.text : undefined} aria-live={action.kind === 'copy' ? 'polite' : undefined} onClick={() => void press()}>
      {label}
    </Button>
  );
}

/** Dismiss needs a reason: a small dialog with one field, the error inside it. */
export function DismissDialog({ incident, open, onOpenChange }: { incident: Incident; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const field = useId();
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await dismissIncident(incident.id, reason);
      onOpenChange(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Dismiss incident"
      description={incident.title}
      dismissible={!busy}
      initialFocus={() => document.getElementById(field)}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Keep it open
          </Button>
          <Button variant="primary" loading={busy} disabled={!reason.trim()} onClick={() => void submit()}>
            Dismiss incident
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.trim()) void submit();
        }}
      >
        <Input id={field} label="Reason" hint="Kept with the incident, so a repeat shows why it was set aside." value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} />
        {error ? <ErrorState variant="inline" error={error} /> : null}
      </form>
    </Dialog>
  );
}

/** The mission Orgus runs for the incident (ORGU-92), as a link in the opened row. */
function MissionLink({ incident }: { incident: Incident }) {
  const navigate = useNavigate();
  const to = handlingPath(incident);
  if (!to) return null;
  return (
    <Button size="sm" variant="ghost" icon={<ArrowUpRight />} onClick={() => navigate(to)}>
      Open the mission
    </Button>
  );
}

/** ORGU-299: The fix session Orgus runs for the incident, as a link in the opened row. */
function FixSessionLink({ incident }: { incident: Incident }) {
  const navigate = useNavigate();
  const to = fixSessionPath(incident);
  if (!to) return null;
  return (
    <Button size="sm" variant="ghost" icon={<ArrowUpRight />} onClick={() => navigate(to)}>
      Open fix session
    </Button>
  );
}

/** ORGU-299: The revert commit Orgus landed for the incident, as a link in the opened row. */
function RevertLink({ incident }: { incident: Incident }) {
  const navigate = useNavigate();
  const to = revertPath(incident);
  if (!to) return null;
  return (
    <Button size="sm" variant="ghost" icon={<ArrowUpRight />} onClick={() => navigate(to)}>
      Open revert
    </Button>
  );
}

/** The opened row: why in full, the evidence, the command to copy, when it opened, and the action in words. */
export function IncidentDetails({ incident, me, id, action }: { incident: Incident; me: string | null; id: string; action?: ReactNode }) {
  // Who is responsible is the row's caption once it is open, so it is not said again here.
  const facts = detailFacts(incident, me).filter((f) => f.label !== 'Responsible');
  const raised = raisedText(incident.count);
  const a = actionOf(incident.action);
  const machineCmd = machineCommandOf(incident.action);
  return (
    <div id={id} className="flex gap-3 px-3 pb-3">
      {/* Under the title, not the tile. */}
      <span className="w-7 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="text-caption text-fg-muted">{incident.why}</p>
        <dl className="grid min-w-0 gap-y-1 text-caption">
          {facts.map((f) => (
            <div key={f.label} className="flex min-w-0 gap-2">
              <dt className="shrink-0 text-fg-subtle">{f.label}</dt>
              <dd className="min-w-0 truncate text-fg-muted num" title={f.value}>
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
        {a.kind === 'copy' && <p className="break-all font-mono text-caption text-fg-muted">{a.text}</p>}
        {machineCmd && (
          <details className="text-caption text-fg-subtle">
            <summary className="cursor-pointer select-none">At the machine</summary>
            <p className="mt-1 break-all font-mono text-fg-muted">{machineCmd}</p>
          </details>
        )}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          {action}
          <MissionLink incident={incident} />
          <FixSessionLink incident={incident} />
          <RevertLink incident={incident} />
          <p className="flex flex-wrap items-center gap-x-2 text-caption text-fg-subtle">
            <RelativeTime ts={incident.openedAt} prefix="Opened" />
            {raised && <span className="num">{raised}</span>}
          </p>
        </div>
      </div>
    </div>
  );
}

/** The caption line: why it matters while open; who took it once taken. */
export function incidentCaption(i: Incident, me: string | null): string {
  // ORGU-92: what Orgus does about it, or why it gave up; with People, why it matters.
  if (i.state === 'open') return i.handling && i.handling.state !== 'people' ? i.handling.text : i.why;
  const by = whoLabel(i.by, me);
  return by ? `${INCIDENT_STATE_LABEL[i.state]} by ${by}` : INCIDENT_STATE_LABEL[i.state];
}

export interface IncidentRowProps {
  incident: Incident;
  now: number;
  me: string | null;
  can: boolean;
  expanded: boolean;
  onToggle: () => void;
  /** A notice's "View incident" pointed here: the row scrolls itself into view. */
  linked?: boolean;
}

export function IncidentRow({ incident, now, me, can, expanded, onToggle, linked }: IncidentRowProps) {
  const navigate = useNavigate();
  const { busy, error, run, clear } = useRun();
  const [dismissing, setDismissing] = useState(false);
  const ref = useRef<HTMLLIElement | null>(null);
  const status = incidentRowStatus(incident);
  const meta = incidentMeta(incident, now, me);
  const primary = primaryActionOf(incident);
  const own = actionOf(incident.action);
  const detailId = useId();
  const Mark = DETECTOR_ICON[incident.detector] ?? TriangleAlert;
  useEffect(() => {
    if (linked) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [linked]);
  const perform = (a: PrimaryAction) => {
    if (a.kind === 'open') return navigate(a.to);
    if (a.kind === 'copy') return void run('primary', async () => {
      if (!(await copyText(a.text))) throw new Error('The browser blocked the clipboard. Open the row and copy the command from there.');
    });
    if (a.kind === 'act') return void run('primary', () => actOnIncident(incident.id));
    return void run('primary', () => ackIncident(incident.id));
  };
  // The menu holds what the row does not show: the own action once taken (the row shows it only
  // while open), Acknowledge, then Dismiss. A taken incident's 'ack' action has nothing left to do.
  const ownItem = !primary && own.kind !== 'ack';
  // The opened row says the action in words: the row's own, or the one a taken incident keeps.
  const inWords = can ? (primary ?? (ownItem ? own : null)) : null;
  const ackItem = incident.state === 'open' && own.kind !== 'ack';
  return (
    <li ref={ref} data-incident={incident.id} className={cn('motion-arrive flex flex-col rounded-control transition-colors duration-(--duration-fast)', expanded ? 'bg-hover' : 'hover:bg-hover')}>
      <div className={cn(REVEAL_GROUP, 'relative flex items-center', expanded ? 'min-h-row-2line' : 'h-row-2line')} data-row>
        <Pressable
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={expanded ? detailId : undefined}
          className={cn('group flex min-w-0 flex-1 items-center gap-3 self-stretch rounded-control px-3', expanded && 'py-2')}
        >
          <span className="flex shrink-0" role="img" aria-label={status.label}>
            <IconTile icon={<Mark />} tone={tileTone(status.tone)} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className={cn('text-ui font-medium text-fg', !expanded && 'truncate')}>{incident.title}</span>
            <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
              <span className="min-w-0 truncate" data-meta>
                {expanded ? incident.responsible.label : incidentCaption(incident, me)}
              </span>
              <span className="shrink-0 num" data-meta title={`Open for ${meta.age}`}>
                <RelativeTime ts={incident.openedAt} />
              </span>
            </span>
          </span>
          <RevealRest className="text-fg-subtle">
            <ChevronRight className={cn('size-3.5 transition-transform duration-(--duration-fast)', expanded && 'rotate-90')} aria-hidden />
          </RevealRest>
        </Pressable>
        {can && (
          <RevealSlot className="absolute right-3 flex items-center gap-1">
            {primary && !expanded && <IncidentActionButton compact action={primary} busy={busy === 'primary'} onRun={() => perform(primary)} />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton size="xs" label="More actions" icon={<Ellipsis />} loading={busy === 'ack'} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {ownItem && <DropdownMenuItem onSelect={() => perform(own)}>{own.label}</DropdownMenuItem>}
                {ackItem && <DropdownMenuItem onSelect={() => void run('ack', () => ackIncident(incident.id))}>Acknowledge</DropdownMenuItem>}
                {(ownItem || ackItem) && <DropdownMenuSeparator />}
                <DropdownMenuItem onSelect={() => setDismissing(true)}>Dismiss incident</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </RevealSlot>
        )}
      </div>
      {error ? (
        <div className="px-3 pb-2">
          <ErrorState variant="inline" error={error} action={<Button size="sm" variant="ghost" onClick={clear}>Hide error</Button>} />
        </div>
      ) : null}
      {expanded && (
        <IncidentDetails
          incident={incident}
          me={me}
          id={detailId}
          action={inWords ? <IncidentActionButton action={inWords} busy={busy === 'primary'} onRun={() => perform(inWords)} /> : null}
        />
      )}
      {can && <DismissDialog incident={incident} open={dismissing} onOpenChange={setDismissing} />}
    </li>
  );
}

/**
 * The watch's state in the panel header, small and dim: when it last checked; a stale watch as an
 * amber mark and the time it stopped (the whole story in the tooltip); "Watch off" when this hub
 * runs none.
 */
export function WatchCaption({ view, stale }: { view: WatchView; stale: boolean }) {
  const now = useNow(1_000);
  if (view.kind === 'absent') {
    return (
      <Tooltip content="The watch is not running on this hub, so its problems are not raised here. Update the hub and restart it with orgus up.">
        <span tabIndex={0} className="whitespace-nowrap rounded-control px-2 text-caption text-fg-subtle">
          Watch off
        </span>
      </Tooltip>
    );
  }
  if (view.kind !== 'ready') return null;
  const at = view.state.lastTickAt;
  if (stale && at != null) {
    const t = staleText(at, now);
    return (
      <Tooltip content={`${t.title}. ${t.body}`}>
        <span tabIndex={0} className="flex items-center gap-1 whitespace-nowrap rounded-control px-2 text-caption text-fg-subtle">
          <TriangleAlert className="size-3 text-status-attention" aria-hidden />
          {/* On a phone the mark alone, so the count beside the title keeps its room. */}
          <span className="max-sm:sr-only">{staleCaption(at, now)}</span>
        </span>
      </Tooltip>
    );
  }
  return <span className="whitespace-nowrap px-2 text-caption text-fg-subtle num max-sm:hidden">{lastCheckedText(at, now)}</span>;
}
